import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import Grid from '@material-ui/core/Grid';
import { BaseModal } from 'components/UI';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'components/FormFields';
import { InvoiceActions } from 'state/modules/invoice';
import { InvoiceTypeSelect } from 'components/AsyncFields';
import InputAdornment from '@material-ui/core/InputAdornment';
import { parseInputNumber } from 'utils';
import { INPUT_REGEX } from 'constants';

export const InvoiceItemModal = ({ open, invoice, handleClick, current, loading }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();

  const formSchema = Yup.object().shape({
    invoice_item_type_id: Yup.number().required(i18n.t('common.required_field')),
    description: Yup.string().matches(INPUT_REGEX, i18n.t('common.input_check')),
    unit_price: Yup.number().required(i18n.t('common.required_field')),
    quantity: Yup.string().required(i18n.t('common.required_field')),
  });

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  const initialValues = {
    invoice_item_type_id: current?.invoice_item_type?.id || '',
    description: current?.description || '',
    unit_price: current?.unit_price ? Math.abs((current.unit_price / 100).toFixed(2)) : 0,
    quantity: current?.quantity || 1,
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      loading={loading}
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      handleClick={handleClick}
      enterButtonLabel={i18n.t(current ? 'common.update' : 'common.add')}
      title={i18n.t(current ? 'invoice.edit_item' : 'invoice.add_new_item')}
      data-cy="invoice-item-modal"
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={(values) => {
          let payload = {
            ...values,
            unit_price: parseInputNumber(values.unit_price),
          };

          if (current) {
            dispatch(InvoiceActions.fetchUpdateInvoiceItem(current?.id, payload, handleClick));
          } else {
            payload = {
              ...payload,
              invoice_id: parseInt(invoice),
            };
            dispatch(InvoiceActions.fetchAddItemToInvoice(payload, handleClick));
          }
        }}
        validationSchema={formSchema}
      >
        {({ handleSubmit, setFieldValue, values }) => (
          <Form noValidate onSubmit={handleSubmit} autoComplete="off">
            <Grid data-cy="invoice-item-modal" container spacing={2}>
              <Grid item xs={12}>
                <Field
                  margin="none"
                  id="AdHocSelect"
                  name="invoice_item_type_id"
                  component={InvoiceTypeSelect}
                  label={i18n.t('common.name')}
                  customParams={{
                    manual: true,
                    is_account_level: false,
                  }}
                  onChange={(value) => {
                    if (value && value?.id !== initialValues?.invoice_item_type_id) {
                      setFieldValue('description', value?.description || '');
                      setFieldValue('quantity', value?.default_quantity || 1);
                      setFieldValue(
                        'unit_price',
                        ((value?.default_unit_price || 0) / 100).toFixed(2)
                      );
                    }
                  }}
                />
              </Grid>
              {(values?.invoice_item_type_id || current) && (
                <>
                  <Grid item xs={12}>
                    <Field
                      type="text"
                      margin="none"
                      name="description"
                      component={TextField}
                      label={i18n.t('common.description')}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      min={1}
                      step="any"
                      type="number"
                      margin="none"
                      format="price"
                      name="unit_price"
                      component={TextField}
                      label={i18n.t('invoice.unit_price')}
                      startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      type="number"
                      margin="none"
                      name="quantity"
                      component={TextField}
                      label={i18n.t('invoice.quantity')}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

InvoiceItemModal.propTypes = {
  current: PropTypes.object,
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

InvoiceItemModal.defaultProps = {
  open: false,
  handleClick: () => {},
};
